import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Fade,
  Grid,
  IconButton,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ParsedDataDisplay from "./ParsedDataDisplay";

const ImagePreviewModal = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const CloseButton = styled(IconButton)({
  position: "absolute",
  right: 16,
  top: 16,
  color: "white",
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },
});

const DataLabelingDashboard = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const filterToUserId = searchParams.get("filter_to_user_id");

  const [assignmentData, setAssignmentData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [cleanedMessages, setCleanedMessages] = useState([]);
  const [expandedMessages, setExpandedMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoSaveStatus, setAutoSaveStatus] = useState("");
  const autoSaveTimer = useRef(null);
  const [messagesModified, setMessagesModified] = useState(false);
  const [prevId, setPrevId] = useState(null);
  const [nextId, setNextId] = useState(null);
  const [currentMessage, setCurrentMessage] = useState("");
  const [currentCleanedMessage, setCurrentCleanedMessage] = useState("");
  const [currentExpandedMessage, setCurrentExpandedMessage] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingCleanedIndex, setEditingCleanedIndex] = useState(null);
  const [editingExpandedIndex, setEditingExpandedIndex] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [editingCleanedText, setEditingCleanedText] = useState("");
  const [editingExpandedText, setEditingExpandedText] = useState("");
  const [strategy, setStrategy] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [jumpToIndex, setJumpToIndex] = useState("");
  const [jumpError, setJumpError] = useState("");

  const fetchAssignment = async (assignmentId, jumpIndex = null) => {
    setLoading(true);
    try {
      const url = new URL("/api/get_assignment/", window.location.origin);
      if (assignmentId) {
        url.searchParams.append("id", assignmentId);
      }
      if (filterToUserId) {
        url.searchParams.append("filter_to_user_id", filterToUserId);
      }
      if (jumpIndex !== null) {
        url.searchParams.append("jump_to_index", jumpIndex);
      }

      const response = await axios.get(url.toString());
      const data = response.data;
      setAssignmentData(data);

      // Set both raw and cleaned messages
      setMessages(data.raw_messages || []);
      setCleanedMessages(data.cleaned_messages || []);
      setExpandedMessages(data.expanded_messages || []);
      setStrategy(data.strategy || "");

      setPrevId(data.prev_id);
      setNextId(data.next_id);
      setJumpError("");
    } catch (error) {
      console.error("Error fetching assignment:", error);
      if (error.response?.data?.error) {
        setJumpError(error.response.data.error);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAssignment(id);
  }, [id]);

  const saveMessages = async () => {
    if (!assignmentData) return;
    setAutoSaveStatus("Saving...");

    const payload = { 
      raw_messages: messages, 
      strategy: strategy,
      expanded_messages: expandedMessages,
    };

    // Add filter_to_user_id to the payload if it exists
    if (filterToUserId) {
      payload.filter_to_user_id = filterToUserId;
      payload.cleaned_messages = cleanedMessages;
      payload.is_admin_view = true;
    }

    try {
      const response = await axios.post(
        `/api/save_labels/${assignmentData.assignment_id}/`,
        payload
      );
      if (response.data.success) {
        setAutoSaveStatus("Saved");
      } else {
        setAutoSaveStatus("Error saving");
      }
    } catch (error) {
      console.error("Error saving labels:", error);
      setAutoSaveStatus("Error saving");
    }
  };

  useEffect(() => {
    if (!messagesModified) return;

    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }
    autoSaveTimer.current = setTimeout(() => {
      saveMessages();
    }, 3000);
    return () => clearTimeout(autoSaveTimer.current);
  }, [messages, cleanedMessages, strategy, expandedMessages, messagesModified]);

  const navigateAssignment = (newId) => {
    // Create a new URLSearchParams object from the current search params
    const params = new URLSearchParams(searchParams.toString());

    // Build the base URL with the new ID
    const baseUrl = `/data-labeling/dashboard/${newId}`;

    // Append all query parameters

    const queryString = params.toString();
    const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

    if (messagesModified) {
      saveMessages().then(() => {
        setMessagesModified(false);
        navigate(url);
      });
    } else {
      navigate(url);
    }
  };

  const handleAddMessage = () => {
    if (currentMessage.trim() && messages.length < 5) {
      setMessages([...messages, currentMessage.trim()]);
      setCurrentMessage("");
      setMessagesModified(true);
    }
  };

  const handleStrategyChange = (event) => {
    setStrategy(event.target.value);
    setMessagesModified(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAddMessage();
    }
  };

  const handleEditClick = (index) => {
    setEditingIndex(index);
    setEditingText(messages[index]);
  };

  const handleEditSave = (index) => {
    if (editingText.trim()) {
      const newMessages = [...messages];
      newMessages[index] = editingText.trim();
      setMessages(newMessages);
      setMessagesModified(true);
    }
    setEditingIndex(null);
    setEditingText("");
  };

  const handleEditKeyPress = (e, index) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleEditSave(index);
    } else if (e.key === "Escape") {
      setEditingIndex(null);
      setEditingText("");
    }
  };

  const handleDeleteMessage = (index) => {
    const newMessages = messages.filter((_, i) => i !== index);
    setMessages(newMessages);
    setMessagesModified(true);
  };

  const handleAddCleanedMessage = () => {
    if (currentCleanedMessage.trim() && cleanedMessages.length < 5) {
      setCleanedMessages([...cleanedMessages, currentCleanedMessage.trim()]);
      setCurrentCleanedMessage("");
      setMessagesModified(true);
    }
  };

  const handleCleanedKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAddCleanedMessage();
    }
  };

  const handleEditCleanedClick = (index) => {
    setEditingCleanedIndex(index);
    setEditingCleanedText(cleanedMessages[index]);
  };

  const handleEditCleanedSave = (index) => {
    if (editingCleanedText.trim()) {
      const newCleanedMessages = [...cleanedMessages];
      newCleanedMessages[index] = editingCleanedText.trim();
      setCleanedMessages(newCleanedMessages);
      setMessagesModified(true);
    }
    setEditingCleanedIndex(null);
    setEditingCleanedText("");
  };

  const handleEditCleanedKeyPress = (e, index) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleEditCleanedSave(index);
    } else if (e.key === "Escape") {
      setEditingCleanedIndex(null);
      setEditingCleanedText("");
    }
  };

  const handleDeleteCleanedMessage = (index) => {
    const newCleanedMessages = cleanedMessages.filter((_, i) => i !== index);
    setCleanedMessages(newCleanedMessages);
    setMessagesModified(true);
  };

  const handleAddExpandedMessage = () => {
    if (currentExpandedMessage.trim() && expandedMessages.length < 5) {
      setExpandedMessages([...expandedMessages, currentExpandedMessage.trim()]);
      setCurrentExpandedMessage("");
      setMessagesModified(true);
    }
  };

  const handleExpandedKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAddExpandedMessage();
    }
  };

  const handleEditExpandedClick = (index) => {
    setEditingExpandedIndex(index);
    setEditingExpandedText(expandedMessages[index]);
  };

  const handleEditExpandedSave = (index) => {
    if (editingExpandedText.trim()) {
      const newExpandedMessages = [...expandedMessages];
      newExpandedMessages[index] = editingExpandedText.trim();
      setExpandedMessages(newExpandedMessages);
      setMessagesModified(true);
    }
    setEditingExpandedIndex(null);
    setEditingExpandedText("");
  };

  const handleEditExpandedKeyPress = (e, index) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleEditExpandedSave(index);
    } else if (e.key === "Escape") {
      setEditingExpandedIndex(null);
      setEditingExpandedText("");
    }
  };

  const handleDeleteExpandedMessage = (index) => {
    const newExpandedMessages = expandedMessages.filter((_, i) => i !== index);
    setExpandedMessages(newExpandedMessages);
    setMessagesModified(true);
  };

  const handleSkipToggle = async () => {
    if (!assignmentData) return;
    const isSkipping = assignmentData.status !== "skipped";
    setAutoSaveStatus(
      isSkipping ? "Marking as skipped..." : "Unmarking as skipped..."
    );
    try {
      const payload = { skip: isSkipping };

      // Add filter_to_user_id to the payload if it exists
      if (filterToUserId) {
        payload.filter_to_user_id = filterToUserId;
      }

      const response = await axios.post(
        `/api/save_labels/${assignmentData.assignment_id}/`,
        payload
      );
      if (response.data.success) {
        setAutoSaveStatus(
          isSkipping ? "Marked as skipped" : "Unmarked as skipped"
        );
        setAssignmentData({
          ...assignmentData,
          status: response.data.assignment_status,
        });
        if (isSkipping && nextId) {
          navigateAssignment(nextId);
        }
      } else {
        setAutoSaveStatus("Error updating skip status");
      }
    } catch (error) {
      console.error("Error updating skip status:", error);
      setAutoSaveStatus("Error updating skip status");
    }
  };

  const handleImageClick = (imageUrl) => {
    setPreviewImage(imageUrl);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewImage(null);
    setPreviewOpen(false);
  };

  const handleJumpToIndex = () => {
    if (jumpToIndex.trim() === "") return;

    const index = parseInt(jumpToIndex.trim(), 10);
    if (isNaN(index) || index <= 0) {
      setJumpError("Please enter a valid positive number");
      return;
    }

    // Subtract 1 from user input to convert from 1-based to 0-based indexing
    const zeroBasedIndex = index - 1;

    if (messagesModified) {
      saveMessages().then(() => {
        setMessagesModified(false);
        fetchAssignment(null, zeroBasedIndex);
      });
    } else {
      fetchAssignment(null, zeroBasedIndex);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if the focus is on an input or textarea element
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA");

      if (isInputFocused) {
        return; // Don't trigger shortcuts if typing in an input
      }

      switch (event.key) {
        case "ArrowLeft":
          if (prevId) {
            navigateAssignment(prevId);
          }
          break;
        case "ArrowRight":
          if (nextId) {
            navigateAssignment(nextId);
          }
          break;
        case " ": // Space bar
          event.preventDefault(); // Prevent default space bar scroll behavior
          handleSkipToggle();
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [prevId, nextId, navigateAssignment, handleSkipToggle]); // Add dependencies

  const handleJumpKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleJumpToIndex();
    }
  };

  if (loading || !assignmentData) {
    return (
      <Box p={4}>
        <Typography variant="h5">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Grid container style={{ height: "calc(100vh - 100px)" }}>
      <Grid item xs={12} md={4}>
        <Paper
          style={{
            height: "100%",
            padding: "20px",
            overflowY: "auto",
            backgroundColor: "#1e1e1e",
            color: "#fff",
          }}
          elevation={3}
        >
          <ParsedDataDisplay
            parsedData={assignmentData?.parsed_data}
            exampleId={assignmentData?.example_id}
            exampleMessages={assignmentData?.example_messages}
          />
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper
          style={{
            height: "100%",
            padding: "20px",
            overflowY: "auto",
            backgroundColor: "#1e1e1e",
            color: "#fff",
          }}
          elevation={3}
        >
          {assignmentData.images.length === 0 ? (
            <Typography color="inherit">No images available.</Typography>
          ) : assignmentData.images.length === 1 ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "20px",
              }}
            >
              <img
                src={assignmentData.images[0]}
                alt={`Example ${assignmentData.example_id} - 0`}
                onClick={() => handleImageClick(assignmentData.images[0])}
                style={{
                  maxWidth: "100%",
                  maxHeight: "calc(100vh - 180px)",
                  objectFit: "contain",
                  cursor: "pointer",
                }}
              />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ marginBottom: "20px" }}>
              {assignmentData.images.map((imgUrl, idx) => (
                <Grid item xs={6} key={idx}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={imgUrl}
                      alt={`Example ${assignmentData.example_id} - ${idx}`}
                      onClick={() => handleImageClick(imgUrl)}
                      style={{
                        width: "100%",
                        maxHeight: "calc((100vh - 180px) / 2)",
                        objectFit: "contain",
                        cursor: "pointer",
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper
          style={{
            height: "calc(100vh - 150px)",
            padding: "20px",
            backgroundColor: "#1e1e1e",
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
          }}
          elevation={3}
        >
          <Box
            sx={{
              flex: "1 1 auto",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <Typography variant="subtitle1" color="inherit">
              Assignment {assignmentData.index + 1} of {assignmentData.total}
            </Typography>

            <Box
              sx={{
                overflowY: "auto",
                flex: "1 1 auto",
                minHeight: 0,
              }}
            >
              {/* Strategy Input - Moved Above Raw Messages */}
              <Box mt={3}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  color="inherit"
                  sx={{ fontSize: "1rem" }}
                >
                  Strategy
                </Typography>
                <TextField
                  label="Enter strategy"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={strategy}
                  onChange={handleStrategyChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.23)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.4)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255, 255, 255, 0.7)",
                    },
                    "& .MuiInputBase-input": {
                      color: "#fff",
                    },
                  }}
                />
              </Box>

              {/* Raw Messages Section */}
              <Box mt={3}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  color="inherit"
                  sx={{ fontSize: "1rem" }}
                >
                  Raw Messages ({messages.length}/5)
                </Typography>
                <Box mb={2} sx={{ display: "flex", flexDirection: "column" }}>
                  {messages.map((msg, index) => (
                    <Box
                      key={`bubble-${index}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 1,
                        marginBottom: "6px",
                      }}
                    >
                      {editingIndex === index ? (
                        <Box
                          sx={{
                            backgroundColor: "#0084ff",
                            color: "white",
                            padding: "3px",
                            borderRadius: "16px",
                            maxWidth: "75%",
                            wordWrap: "break-word",
                          }}
                        >
                          <TextField
                            autoFocus
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            onKeyDown={(e) => handleEditKeyPress(e, index)}
                            multiline
                            size="small"
                            sx={{
                              "& .MuiInputBase-root": {
                                color: "white",
                                padding: "3px 6px",
                                fontSize: "0.9rem",
                              },
                              "& .MuiInputBase-input": {
                                padding: 0,
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  border: "none",
                                },
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            backgroundColor: "#0084ff",
                            color: "white",
                            padding: "6px 10px",
                            borderRadius: "16px",
                            maxWidth: "75%",
                            wordWrap: "break-word",
                          }}
                        >
                          <Typography sx={{ fontSize: "0.9rem" }}>
                            {msg}
                          </Typography>
                        </Box>
                      )}
                      <IconButton
                        size="small"
                        onClick={() =>
                          editingIndex === index
                            ? handleEditSave(index)
                            : handleEditClick(index)
                        }
                        sx={{
                          color: "rgba(255, 255, 255, 0.7)",
                          padding: "4px",
                        }}
                      >
                        {editingIndex === index ? (
                          <SendIcon
                            fontSize="small"
                            sx={{ fontSize: "1rem" }}
                          />
                        ) : (
                          <EditIcon
                            fontSize="small"
                            sx={{ fontSize: "1rem" }}
                          />
                        )}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (editingIndex === index) {
                            setEditingIndex(null);
                            setEditingText("");
                          } else {
                            handleDeleteMessage(index);
                          }
                        }}
                        sx={{
                          color: "rgba(255, 255, 255, 0.7)",
                          padding: "4px",
                        }}
                      >
                        {editingIndex === index ? (
                          <CloseIcon
                            fontSize="small"
                            sx={{ fontSize: "1rem" }}
                          />
                        ) : (
                          <DeleteIcon
                            fontSize="small"
                            sx={{ fontSize: "1rem" }}
                          />
                        )}
                      </IconButton>
                    </Box>
                  ))}
                </Box>
                <Box mb={2} sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    label="New raw message"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    value={currentMessage}
                    onChange={(e) => setCurrentMessage(e.target.value)}
                    onKeyDown={handleKeyPress}
                    disabled={messages.length >= 5}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "rgba(255, 255, 255, 0.23)",
                        },
                        "&:hover fieldset": {
                          borderColor: "rgba(255, 255, 255, 0.4)",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "rgba(255, 255, 255, 0.7)",
                      },
                      "& .MuiInputBase-input": {
                        color: "#fff",
                      },
                    }}
                  />
                  <IconButton
                    onClick={handleAddMessage}
                    disabled={!currentMessage.trim() || messages.length >= 5}
                    sx={{
                      alignSelf: "flex-end",
                      color: "primary.main",
                      "&.Mui-disabled": {
                        color: "rgba(255, 255, 255, 0.3)",
                      },
                    }}
                  >
                    <SendIcon />
                  </IconButton>
                </Box>
              </Box>

              {filterToUserId && (
                <Box mt={3}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    color="inherit"
                    sx={{ fontSize: "1rem" }}
                  >
                    Cleaned Messages ({cleanedMessages.length}/5)
                  </Typography>
                  <Box mb={2} sx={{ display: "flex", flexDirection: "column" }}>
                    {cleanedMessages.map((msg, index) => (
                      <Box
                        key={`cleaned-bubble-${index}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: 1,
                          marginBottom: "6px",
                        }}
                      >
                        {editingCleanedIndex === index ? (
                          <Box
                            sx={{
                              backgroundColor: "#00a86b",
                              color: "white",
                              padding: "3px",
                              borderRadius: "16px",
                              maxWidth: "75%",
                              wordWrap: "break-word",
                            }}
                          >
                            <TextField
                              autoFocus
                              value={editingCleanedText}
                              onChange={(e) =>
                                setEditingCleanedText(e.target.value)
                              }
                              onKeyDown={(e) =>
                                handleEditCleanedKeyPress(e, index)
                              }
                              multiline
                              size="small"
                              sx={{
                                "& .MuiInputBase-root": {
                                  color: "white",
                                  padding: "3px 6px",
                                  fontSize: "0.9rem",
                                },
                                "& .MuiInputBase-input": {
                                  padding: 0,
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    border: "none",
                                  },
                              }}
                            />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              backgroundColor: "#00a86b",
                              color: "white",
                              padding: "6px 10px",
                              borderRadius: "16px",
                              maxWidth: "75%",
                              wordWrap: "break-word",
                            }}
                          >
                            <Typography sx={{ fontSize: "0.9rem" }}>
                              {msg}
                            </Typography>
                          </Box>
                        )}
                        <IconButton
                          size="small"
                          onClick={() =>
                            editingCleanedIndex === index
                              ? handleEditCleanedSave(index)
                              : handleEditCleanedClick(index)
                          }
                          sx={{
                            color: "rgba(255, 255, 255, 0.7)",
                            padding: "4px",
                          }}
                        >
                          {editingCleanedIndex === index ? (
                            <SendIcon
                              fontSize="small"
                              sx={{ fontSize: "1rem" }}
                            />
                          ) : (
                            <EditIcon
                              fontSize="small"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (editingCleanedIndex === index) {
                              setEditingCleanedIndex(null);
                              setEditingCleanedText("");
                            } else {
                              handleDeleteCleanedMessage(index);
                            }
                          }}
                          sx={{
                            color: "rgba(255, 255, 255, 0.7)",
                            padding: "4px",
                          }}
                        >
                          {editingCleanedIndex === index ? (
                            <CloseIcon
                              fontSize="small"
                              sx={{ fontSize: "1rem" }}
                            />
                          ) : (
                            <DeleteIcon
                              fontSize="small"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                  <Box mb={2} sx={{ display: "flex", gap: 1 }}>
                    <TextField
                      label="New cleaned message"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={3}
                      value={currentCleanedMessage}
                      onChange={(e) => setCurrentCleanedMessage(e.target.value)}
                      onKeyDown={handleCleanedKeyPress}
                      disabled={cleanedMessages.length >= 5}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "rgba(255, 255, 255, 0.23)",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgba(255, 255, 255, 0.4)",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "rgba(255, 255, 255, 0.7)",
                        },
                        "& .MuiInputBase-input": {
                          color: "#fff",
                        },
                      }}
                    />
                    <IconButton
                      onClick={handleAddCleanedMessage}
                      disabled={
                        !currentCleanedMessage.trim() ||
                        cleanedMessages.length >= 5
                      }
                      sx={{
                        alignSelf: "flex-end",
                        color: "primary.main",
                        "&.Mui-disabled": {
                          color: "rgba(255, 255, 255, 0.3)",
                        },
                      }}
                    >
                      <SendIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {/* Expanded Messages Section */}
              <Box mt={3}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  color="inherit"
                  sx={{ fontSize: "1rem" }}
                >
                  Expanded Messages ({expandedMessages.length}/5)
                </Typography>
                <Box mb={2} sx={{ display: "flex", flexDirection: "column" }}>
                  {expandedMessages.map((msg, index) => (
                    <Box
                      key={`expanded-bubble-${index}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 1,
                        marginBottom: "6px",
                      }}
                    >
                      {editingExpandedIndex === index ? (
                        <Box
                          sx={{
                            backgroundColor: "#9c27b0",
                            color: "white",
                            padding: "3px",
                            borderRadius: "16px",
                            maxWidth: "75%",
                            wordWrap: "break-word",
                          }}
                        >
                          <TextField
                            autoFocus
                            value={editingExpandedText}
                            onChange={(e) => setEditingExpandedText(e.target.value)}
                            onKeyDown={(e) => handleEditExpandedKeyPress(e, index)}
                            multiline
                            size="small"
                            sx={{
                              "& .MuiInputBase-root": {
                                color: "white",
                                padding: "3px 6px",
                                fontSize: "0.9rem",
                              },
                              "& .MuiInputBase-input": {
                                padding: 0,
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            backgroundColor: "#9c27b0",
                            color: "white",
                            padding: "6px 10px",
                            borderRadius: "16px",
                            maxWidth: "75%",
                            wordWrap: "break-word",
                          }}
                        >
                          <Typography sx={{ fontSize: "0.9rem" }}>
                            {msg}
                          </Typography>
                        </Box>
                      )}
                      <IconButton
                        size="small"
                        onClick={() =>
                          editingExpandedIndex === index
                            ? handleEditExpandedSave(index)
                            : handleEditExpandedClick(index)
                        }
                        sx={{
                          color: "rgba(255, 255, 255, 0.7)",
                          padding: "4px",
                        }}
                      >
                        {editingExpandedIndex === index ? (
                          <SendIcon fontSize="small" sx={{ fontSize: "1rem" }} />
                        ) : (
                          <EditIcon fontSize="small" sx={{ fontSize: "1rem" }} />
                        )}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (editingExpandedIndex === index) {
                            setEditingExpandedIndex(null);
                            setEditingExpandedText("");
                          } else {
                            handleDeleteExpandedMessage(index);
                          }
                        }}
                        sx={{
                          color: "rgba(255, 255, 255, 0.7)",
                          padding: "4px",
                        }}
                      >
                        {editingExpandedIndex === index ? (
                          <CloseIcon fontSize="small" sx={{ fontSize: "1rem" }} />
                        ) : (
                          <DeleteIcon fontSize="small" sx={{ fontSize: "1rem" }} />
                        )}
                      </IconButton>
                    </Box>
                  ))}
                </Box>
                <Box mb={2} sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    label="New expanded message"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    value={currentExpandedMessage}
                    onChange={(e) => setCurrentExpandedMessage(e.target.value)}
                    onKeyDown={handleExpandedKeyPress}
                    disabled={expandedMessages.length >= 5}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "rgba(255, 255, 255, 0.23)",
                        },
                        "&:hover fieldset": {
                          borderColor: "rgba(255, 255, 255, 0.4)",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "rgba(255, 255, 255, 0.7)",
                      },
                      "& .MuiInputBase-input": {
                        color: "#fff",
                      },
                    }}
                  />
                  <IconButton
                    onClick={handleAddExpandedMessage}
                    disabled={
                      !currentExpandedMessage.trim() || expandedMessages.length >= 5
                    }
                    sx={{
                      alignSelf: "flex-end",
                      color: "primary.main",
                      "&.Mui-disabled": {
                        color: "rgba(255, 255, 255, 0.3)",
                      },
                    }}
                  >
                    <SendIcon />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            mt={3}
            sx={{
              borderTop: "1px solid rgba(255, 255, 255, 0.1)",
              paddingTop: 2,
              flex: "0 0 auto",
            }}
          >
            <Typography variant="body1" color="inherit">
              {autoSaveStatus}
            </Typography>

            <Box mt={2} display="flex" alignItems="center" gap={2}>
              <Button
                variant="contained"
                onClick={() => navigateAssignment(prevId)}
                disabled={!prevId}
              >
                Previous
              </Button>
              <Button
                variant="contained"
                onClick={() => navigateAssignment(nextId)}
                disabled={!nextId}
              >
                Next
              </Button>

              <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
                <TextField
                  label="Jump to #"
                  variant="outlined"
                  size="small"
                  value={jumpToIndex}
                  onChange={(e) => {
                    // Only allow numeric input
                    const numericValue = e.target.value.replace(/[^0-9]/g, "");
                    setJumpToIndex(numericValue);
                    setJumpError("");
                  }}
                  onKeyDown={handleJumpKeyPress}
                  error={!!jumpError}
                  helperText={jumpError}
                  sx={{
                    width: "100px",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.23)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.4)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255, 255, 255, 0.7)",
                      fontSize: "0.8rem",
                    },
                    "& .MuiInputBase-input": {
                      color: "#fff",
                      padding: "8px 12px",
                    },
                    "& .MuiFormHelperText-root": {
                      color: "error.main",
                      position: "absolute",
                      bottom: "-20px",
                    },
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleJumpToIndex}
                  sx={{ ml: 1 }}
                >
                  Go
                </Button>
              </Box>
            </Box>

            <Box
              mt={2}
              mb={1}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <Button
                variant="contained"
                color={
                  assignmentData.status === "skipped" ? "warning" : "secondary"
                }
                onClick={handleSkipToggle}
              >
                {assignmentData.status === "skipped"
                  ? "Unskip Assignment"
                  : "Mark as Skipped"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Grid>

      <ImagePreviewModal
        open={previewOpen}
        onClose={handleClosePreview}
        closeAfterTransition
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              transition: "opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)",
            },
          },
        }}
      >
        <Fade
          in={previewOpen}
          timeout={{
            enter: 300,
            exit: 200,
          }}
        >
          <Box
            sx={{
              outline: "none",
              position: "relative",
            }}
          >
            <CloseButton
              onClick={(e) => {
                e.stopPropagation();
                handleClosePreview();
              }}
              aria-label="Close preview"
            >
              <CloseIcon />
            </CloseButton>
            {previewImage && (
              <Box
                component="img"
                src={previewImage}
                sx={{
                  maxWidth: "90vw",
                  maxHeight: "80vh",
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  borderRadius: "8px",
                  transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                  pointerEvents: "none",
                }}
              />
            )}
          </Box>
        </Fade>
      </ImagePreviewModal>
    </Grid>
  );
};

export default DataLabelingDashboard;
